import { useState, useEffect, useRef } from "react";
import { NAVY, DANGER } from "../styles/palette";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { ClickableDate } from "./ClickableDate";
import { ACTIVE_THEME } from "../themes";
import { getPrice, getDeoMonthly } from "../lib/pricing";
import { useIsMobile } from "../lib/useIsMobile";
import { localDateStr } from "../lib/date";

// Normalize a raw cleanup/deo status into one of four buckets the Subscriptions
// tab filters + badges on. A deactivated client (active===false) cancels its subs.
const normSubStatus = (raw, clientGone) => {
  if (clientGone || raw === "deactivated" || raw === "canceled" || raw === "cancelled") return "canceled";
  if (raw === "paused") return "paused";
  if (raw === "payment_pending" || raw === "past_due") return "past_due";
  return "active";
};
// Why a tech's hours can be short — shown as a title on desktop, a tappable toast + footnote on phones.
const INCOMPLETE_HOURS_NOTE = "Some jobs this period have no start/end time (e.g. marked done manually) and aren't counted toward hours.";
const SUB_BADGE = {
  active:   { bg: "#dcfce7",             color: "#166534", label: "Active" },
  paused:   { bg: "rgba(178,107,0,.12)", color: "#B26B00", label: "Paused" },
  past_due: { bg: "#ffebe6",             color: "#bf2600", label: "Past due" },
  canceled: { bg: "#f4f5f7",             color: "#42526e", label: "Canceled" },
};

// Hand-rolled SVG trend chart (no library) — line + flat fill + dashed prior-period ghost
// line + a crosshair tooltip on hover. Reusable for any per-day money series.
function TrendChart({ series, prevSeries, compareOn, compareLabel, fmt }) {
  const [hi, setHi] = useState(null);
  const clearTimer = useRef(null);
  useEffect(() => () => clearTimeout(clearTimer.current), []);
  if (!series || series.length < 2) return null;
  const H = 150, PAD = 8, VW = 1000, n = series.length;
  const max = Math.max(1, ...series.map(p => p.amount), ...(compareOn ? prevSeries.map(p => p.amount) : []));
  const xPct = (i) => (n <= 1 ? 0 : (i / (n - 1)) * 100);
  const xVB = (i) => (xPct(i) / 100) * VW;
  const yVB = (v) => H - PAD - (v / max) * (H - 2 * PAD);
  const poly = (arr) => arr.map((p, i) => `${xVB(i).toFixed(1)},${yVB(p.amount).toFixed(1)}`).join(" ");
  const line = poly(series);
  const ghost = compareOn && prevSeries.length === n ? poly(prevSeries) : null;
  const moveAt = (clientX, el) => { const r = el.getBoundingClientRect(); const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width)); setHi(Math.round(f * (n - 1))); };
  const onMove = (e) => moveAt(e.clientX, e.currentTarget);
  // Touch: a finger scrubs the crosshair (touchAction pan-y keeps vertical page scroll
  // working); the last point stays readable for a moment after lift-off, then clears.
  const onTouch = (e) => { clearTimeout(clearTimer.current); const t = e.touches && e.touches[0]; if (t) moveAt(t.clientX, e.currentTarget); };
  const onTouchEnd = () => { clearTimeout(clearTimer.current); clearTimer.current = setTimeout(() => setHi(null), 2500); };
  const cur = hi != null ? series[hi] : null;
  const pAt = hi != null && compareOn && prevSeries[hi] ? prevSeries[hi] : null;
  return (
    <div style={{ position: "relative", marginTop: 8, marginBottom: 18, touchAction: "pan-y" }} onMouseMove={onMove} onMouseLeave={() => setHi(null)}
      onTouchStart={onTouch} onTouchMove={onTouch} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}>
      <svg viewBox={`0 0 ${VW} ${H}`} preserveAspectRatio="none" width="100%" height={H} style={{ display: "block" }}>
        <line x1="0" y1={H - PAD} x2={VW} y2={H - PAD} stroke="rgba(0,0,0,.06)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        <polygon points={`0,${H} ${line} ${VW},${H}`} fill="rgba(0,113,227,.10)" />
        {ghost && <polyline points={ghost} fill="none" stroke="#A1A1A6" strokeWidth="1.5" strokeDasharray="5 4" opacity=".5" vectorEffect="non-scaling-stroke" />}
        <polyline points={line} fill="none" stroke="var(--accent)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        {hi != null && <line x1={xVB(hi)} y1="0" x2={xVB(hi)} y2={H} stroke="rgba(0,0,0,.18)" strokeWidth="1" vectorEffect="non-scaling-stroke" />}
      </svg>
      {hi != null && <div style={{ position: "absolute", left: `${xPct(hi)}%`, top: yVB(series[hi].amount), width: 8, height: 8, margin: "-4px 0 0 -4px", borderRadius: "50%", background: "var(--accent)", pointerEvents: "none" }} />}
      {cur && (
        <div style={{ position: "absolute", top: -4, left: `${xPct(hi)}%`, transform: `translateX(${hi > n / 2 ? "-108%" : "8%"})`, pointerEvents: "none", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.14)", padding: "7px 10px", fontSize: 12, whiteSpace: "nowrap", zIndex: 5 }}>
          <div style={{ color: "var(--text2)" }}>{new Date(cur.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
          <div style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt(cur.amount)}</div>
          {pAt && <div style={{ fontSize: 11, color: "var(--text3)" }}>vs {fmt(pAt.amount)} {compareLabel}</div>}
        </div>
      )}
    </div>
  );
}

// Phone layout for the report tables. A 5-6 column <table> in a ~325px pane hides the
// money column behind a side-scroll, so on phones each row is a two-line card: primary
// text (+ optional pill) with the amount right-aligned on line 1, muted meta on line 2,
// and any extra content (a wrapping note, the payroll rate input) below.
function MobileRow({ title, badge, amount, amountColor, meta, children }) {
  return (
    <div style={{display:"flex",alignItems:"flex-start",gap:12,padding:"12px 0",borderBottom:"1px solid var(--border)"}}>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0,flexWrap:"wrap"}}>
          <span style={{fontWeight:600,fontSize:14,color:"var(--text)",overflowWrap:"anywhere"}}>{title}</span>
          {badge}
        </div>
        {meta && <div style={{fontSize:12,color:"var(--text2)",marginTop:3,lineHeight:1.4,overflowWrap:"anywhere"}}>{meta}</div>}
        {children}
      </div>
      {amount != null && (
        <div style={{fontWeight:600,fontSize:15,letterSpacing:"-.02em",fontVariantNumeric:"tabular-nums",color:amountColor||"var(--text)",flexShrink:0,whiteSpace:"nowrap"}}>{amount}</div>
      )}
    </div>
  );
}
// Stripe IDs are long opaque strings — on a phone show them small but readable and wrapping.
const mobileIdStyle = {fontSize:11,color:"var(--text3)",marginTop:3,fontVariantNumeric:"tabular-nums",overflowWrap:"anywhere"};

// REPORTS PAGE
export function ReportsPage({ invoices, refunds, credits, technicians, clients, pricing, showToast, onRefreshInvoices }) {
  const isMobile = useIsMobile();
  const [tab,        setTab]        = useState("income");
  const [subStatus,  setSubStatus]  = useState("all"); // Subscriptions tab: status filter
  const [subType,    setSubType]    = useState("all"); // Subscriptions tab: cleanup/deo filter
  const [subSearch,  setSubSearch]  = useState("");    // Subscriptions tab: client / sub-id search
  const [period,     setPeriod]     = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo,   setCustomTo]   = useState(localDateStr());
  const [compareMode, setCompareMode] = useState("period"); // period | year — drives the KPI delta chips (+ chart ghost line)
  const [techHours,  setTechHours]  = useState(
    Object.fromEntries((technicians||[]).map(t=>[t.id,{rate:t.hourly_rate||0}]))
  );
  const [hoursByTech, setHoursByTech] = useState({});         // DERIVED: tech.id -> total worked hours in the period
  const [incompleteTechs, setIncompleteTechs] = useState({}); // tech.id -> true if some day had work but no usable start→end span
  // generating state removed

  // Update techHours when technicians load
  useEffect(()=>{
    setTechHours(Object.fromEntries((technicians||[]).map(t=>[t.id,{rate:t.hourly_rate||0}])));
  },[technicians]); // eslint-disable-line react-hooks/exhaustive-deps

  // Date range
  const now = new Date();
  function getDateRange() {
    if (period==="week")  { const s=new Date(now); s.setDate(now.getDate()-7);  return {from:s.toISOString().split("T")[0], to:now.toISOString().split("T")[0]}; }
    if (period==="month") { return {from:`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`, to:now.toISOString().split("T")[0]}; }
    if (period==="year")  { return {from:`${now.getFullYear()}-01-01`, to:now.toISOString().split("T")[0]}; }
    return {from:customFrom, to:customTo};
  }
  const {from, to} = getDateRange();

  // Hours worked are DERIVED, not entered: for each technician on each day, the span
  // from their FIRST job's started_at to their LAST job's completed_at, summed across
  // the selected period. Reads the jobs table directly (operator JWT scopes to the org).
  useEffect(() => {
    if (!from || !to) { setHoursByTech({}); setIncompleteTechs({}); return; }
    let cancelled = false;
    // Any job with a start OR a completion in the window (manual completions have a
    // completed_at but no started_at; in-progress jobs have a start but no end).
    fetch(`${SUPABASE_URL}/rest/v1/jobs?scheduled_date=gte.${from}&scheduled_date=lte.${to}&or=(started_at.not.is.null,completed_at.not.is.null)&select=technician_id,scheduled_date,started_at,completed_at`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } })
      .then(r => r.json())
      .then(rows => {
        if (cancelled || !Array.isArray(rows)) return;
        // earliest start + latest end per tech-day, compared by epoch ms (offset-safe)
        const span = {}; // `${tech}|${day}` -> { tech, start, end } in ms
        rows.forEach(j => {
          if (!j.technician_id) return;
          const k = j.technician_id + "|" + j.scheduled_date;
          const s = span[k] || (span[k] = { tech: j.technician_id, start: null, end: null });
          const st = j.started_at   ? Date.parse(j.started_at)   : null;
          const en = j.completed_at ? Date.parse(j.completed_at) : null;
          if (st != null && (s.start == null || st < s.start)) s.start = st;
          if (en != null && (s.end   == null || en > s.end))   s.end   = en;
        });
        const totals = {}, incomplete = {};
        Object.values(span).forEach(s => {
          if (s.start != null && s.end != null && s.end > s.start) {
            totals[s.tech] = (totals[s.tech] || 0) + (s.end - s.start) / 3600000;
          } else {
            // work logged but no usable start→end span (manual completion with no
            // start, or a still-in-progress job) — flag so hours aren't silently lost.
            incomplete[s.tech] = true;
          }
        });
        if (!cancelled) { setHoursByTech(totals); setIncompleteTechs(incomplete); }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [from, to]);

  // Filter invoices by date range
  const inRange = (date) => !from || !to || (date>=from && date<=to);

  const paidInvoices    = invoices.filter(i=>i.status==="paid"      && inRange(i.date));
  const rangeRefunds    = refunds.filter(r=>inRange(r.created_at?.split("T")[0]||""));
  // B9: credits are Stripe balance transactions in the `credits` table, NOT
  // negative invoices — the old `invoices.amount < 0` filter was always empty, so
  // "Credits Issued" showed $0 and Net Income was overstated. Sum the credits
  // table instead; positive rows are credits, negative rows are reversals, so a
  // plain signed sum nets reversed credits out automatically.
  const rangeCredits    = (credits||[]).filter(c=>inRange((c.created_at||"").split("T")[0]||""));

  // Credit rows mapped to the table shape the Credits tab / CSV expect.
  // amount: positive = credit applied (a revenue deduction), negative = reversal.
  const creditInvoices = rangeCredits.map(c=>({
    id:             c.id,
    date:           (c.created_at||"").split("T")[0],
    client_name:    c.client_name || "",
    invoice_number: c.job_id ? "Skip credit" : "Manual credit",
    amount:         Number(c.amount||0),
    notes:          c.description || "",
  }));

  // ── Subscriptions (current state) — every client has a cleanup plan; deo is an
  // add-on, so a deo client = 2 rows (one Stripe subscription each). Derived from the
  // synced `clients` data (the webhook keeps status fresh), not a live Stripe call.
  const subs = [];
  (clients||[]).forEach(c => {
    const clientGone = c.active === false;
    subs.push({
      key: c.id + ":cleanup", client_id: c.id, client_name: c.name, type: "cleanup",
      status: normSubStatus(c.status, clientGone), frequency: c.frequency,
      amount: getPrice(c.frequency, c.dogs, pricing), stripe_id: c.stripe_subscription_id || "",
    });
    if (c.deo_enabled) subs.push({
      key: c.id + ":deo", client_id: c.id, client_name: c.name, type: "deo",
      status: normSubStatus(c.deo_status, clientGone), frequency: c.deo_frequency,
      amount: getDeoMonthly(c.deo_frequency, pricing), stripe_id: c.deo_stripe_subscription_id || c.deo_stripe_item_id || "",
    });
  });
  const subStatusCounts = subs.reduce((acc,s)=>{ acc.all++; acc[s.status]=(acc[s.status]||0)+1; return acc; }, {all:0,active:0,paused:0,past_due:0,canceled:0});
  const filteredSubs = subs.filter(s=>{
    if (subStatus!=="all" && s.status!==subStatus) return false;
    if (subType!=="all"   && s.type!==subType)     return false;
    const q = subSearch.trim().toLowerCase();
    return !q || s.client_name.toLowerCase().includes(q) || (s.stripe_id||"").toLowerCase().includes(q);
  }).sort((a,b)=> a.client_name.localeCompare(b.client_name) || a.type.localeCompare(b.type));
  const activeMRR = subs.filter(s=>s.status==="active").reduce((sum,s)=>sum+Number(s.amount||0),0);

  const totalIncome  = paidInvoices.filter(i=>Number(i.amount)>0).reduce((s,i)=>s+Number(i.amount),0);
  const totalCredits = Math.max(0, rangeCredits.reduce((s,c)=>s+Number(c.amount||0),0));
  const totalRefunds = rangeRefunds.reduce((s,r)=>s+Number(r.amount),0);
  const netIncome    = totalIncome - totalCredits - totalRefunds;

  // ── Period-over-period comparison for the KPI delta chips — re-filter the in-memory
  // invoices/refunds/credits over a prior window (previous period, or the same window last
  // year). No new fetch.
  const isoD = (d) => d.toISOString().split("T")[0];
  function prevRange() {
    if (!from || !to) return { from: "", to: "" };
    const f = new Date(from + "T12:00:00"), t = new Date(to + "T12:00:00");
    if (compareMode === "year") {
      const pf = new Date(f); pf.setFullYear(pf.getFullYear() - 1);
      const pt = new Date(t); pt.setFullYear(pt.getFullYear() - 1);
      return { from: isoD(pf), to: isoD(pt) };
    }
    const span = Math.round((t - f) / 86400000);
    const pt = new Date(f); pt.setDate(pt.getDate() - 1);
    const pf = new Date(pt); pf.setDate(pf.getDate() - span);
    return { from: isoD(pf), to: isoD(pt) };
  }
  const prev = prevRange();
  const pIn = (d) => !!prev.from && !!prev.to && d >= prev.from && d <= prev.to;
  const prevIncome  = invoices.filter(i => i.status === "paid" && Number(i.amount) > 0 && pIn(i.date)).reduce((s, i) => s + Number(i.amount), 0);
  const prevCredits = Math.max(0, (credits || []).filter(c => pIn((c.created_at || "").split("T")[0])).reduce((s, c) => s + Number(c.amount || 0), 0));
  const prevRefunds = refunds.filter(r => pIn(r.created_at?.split("T")[0] || "")).reduce((s, r) => s + Number(r.amount), 0);
  const prevNet     = prevIncome - prevCredits - prevRefunds;
  const compareLabel = compareMode === "year" ? "last year" : "prev period";

  // Daily income series for the trend chart (current window + the prior window, index-aligned).
  function dailySeries(rows, getDate, getAmt, f0, t0) {
    if (!f0 || !t0) return [];
    const by = {};
    rows.forEach(r => { const d = getDate(r); if (d && d >= f0 && d <= t0) by[d] = (by[d] || 0) + getAmt(r); });
    const out = [], dd = new Date(f0 + "T12:00:00"), end = new Date(t0 + "T12:00:00");
    let g = 0;
    while (dd <= end && g++ < 800) { const ds = isoD(dd); out.push({ date: ds, amount: by[ds] || 0 }); dd.setDate(dd.getDate() + 1); }
    return out;
  }
  const paidPos = invoices.filter(i => i.status === "paid" && Number(i.amount) > 0);
  const incomeSeries = dailySeries(paidPos, i => i.date, i => Number(i.amount), from, to);
  const incomePrevSeries = dailySeries(paidPos, i => i.date, i => Number(i.amount), prev.from, prev.to);
  const fmtMoney = (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Hours are DERIVED from job timestamps (hoursByTech); only the rate is operator-entered.
  // Gross = worked hours × rate. One set of helpers feeds the table, the footer total, the
  // exports, AND the Tax tab, so every number reconciles.
  const techRate        = (t) => Number((techHours[t.id]||{}).rate || 0);
  const techWorkedHours = (t) => Math.round((hoursByTech[t.id]||0)*10)/10;
  // Round each row to whole cents so the footer total equals the sum of the displayed rows.
  const techGross       = (t) => Math.round(techWorkedHours(t) * techRate(t) * 100) / 100;
  // F3: sum over ACTIVE techs only, matching every rendered table/export.
  const totalWages = (technicians||[]).filter(t=>t.active).reduce((s,t)=>s+techGross(t),0);
  const netProfit  = netIncome - totalWages;

  async function saveTechField(techId, field, val) {
    await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${techId}`,{
      method:"PATCH",
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
      body:JSON.stringify({[field]:Number(val)}),
    });
  }

  function downloadCSV() {
    let csv = "";
    if (tab==="subs") {
      csv = "Client,Type,Frequency,Status,Monthly,Stripe Subscription ID\n";
      filteredSubs.forEach(s=>{ csv += `${s.client_name},${s.type==="deo"?"Deodorizer":"Cleanup"},${s.frequency||""},${SUB_BADGE[s.status].label},$${Number(s.amount||0).toFixed(2)},${s.stripe_id||""}\n`; });
    } else if (tab==="income") {
      csv = "Date,Client,Invoice #,Amount,Type\n";
      paidInvoices.filter(i=>Number(i.amount)>0).forEach(i=>{
        csv += `${i.date},${i.client_name},${i.invoice_number},$${Number(i.amount).toFixed(2)},Income\n`;
      });
      creditInvoices.forEach(i=>{
        const sign = Number(i.amount)>=0 ? "-" : "+";
        csv += `${i.date},${i.client_name},${i.invoice_number},${sign}$${Math.abs(Number(i.amount)).toFixed(2)},${Number(i.amount)>=0?"Credit":"Credit reversal"}\n`;
      });
      rangeRefunds.forEach(r=>{
        csv += `${r.created_at?.split("T")[0]},${r.client_name},${r.invoice_number},-$${Number(r.amount).toFixed(2)},Refund\n`;
      });
    } else if (tab==="payroll") {
      csv = "Technician,Clients,Hours,Rate,Gross Pay\n";
      (technicians||[]).filter(t=>t.active).forEach(t=>{
        const assigned = (clients||[]).filter(c=>c.technician_id===t.id&&c.active).length;
        csv += `${t.name},${assigned},${techWorkedHours(t)},$${techRate(t).toFixed(2)},$${techGross(t).toFixed(2)}\n`;
      });
    } else if (tab==="credits") {
      csv = "Date,Client,Invoice #,Credit Amount,Notes\n";
      creditInvoices.forEach(i=>{
        csv += `${i.date},${i.client_name},${i.invoice_number},${Number(i.amount)>=0?"-":"+"}$${Math.abs(Number(i.amount)).toFixed(2)},${(i.notes||"").replace(/[\n,]/g," ")}\n`;
      });
    } else if (tab==="tax") {
      csv = "Metric,Amount\n";
      csv += `Gross Revenue,$${totalIncome.toFixed(2)}\n`;
      csv += `Credits Issued,-$${totalCredits.toFixed(2)}\n`;
      csv += `Refunds Issued,-$${totalRefunds.toFixed(2)}\n`;
      csv += `Net Revenue,$${netIncome.toFixed(2)}\n`;
      csv += `Total Wages,-$${totalWages.toFixed(2)}\n`;
      csv += `Net Profit,$${netProfit.toFixed(2)}\n`;
    } else {
      // refunds (the only remaining tab with an export button)
      csv = "Date,Client,Invoice #,Amount,Reason\n";
      rangeRefunds.forEach(r=>{
        csv += `${r.created_at?.split("T")[0]},${r.client_name},${r.invoice_number},$${Number(r.amount).toFixed(2)},${r.reason||""}\n`;
      });
    }
    const name = `scoopcrm-${tab}-report-${from}-to-${to}.csv`;
    // Phone: a synthetic <a download> click is unreliable from a home-screen (standalone)
    // Safari session — hand the file to the native share sheet when the browser can, so it
    // can go to Files / Mail / Numbers. share() must run inside the tap, so nothing is awaited
    // before it. Falls back to the anchor download everywhere else.
    if (isMobile && typeof navigator !== "undefined" && navigator.share && navigator.canShare && typeof File === "function") {
      try {
        const file = new File([csv], name, { type: "text/csv" });
        if (navigator.canShare({ files: [file] })) {
          navigator.share({ files: [file], title: name }).catch(() => {});
          return;
        }
      } catch (e) { /* fall through to the anchor download */ }
    }
    const blob = new Blob([csv], {type:"text/csv"});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = name;
    a.click(); URL.revokeObjectURL(url);
  }

  function downloadPDF() {
    const win = window.open("","_blank");
    // iOS standalone mode / pop-up blockers return null; without this guard the handler
    // throws on win.document and the tap silently does nothing.
    if (!win) { if (showToast) showToast("Pop-up blocked — allow pop-ups for this site to export the PDF", "error"); return; }
    const rows = tab==="subs"
      ? filteredSubs.map(s=>`<tr><td>${s.client_name}</td><td>${s.type==="deo"?"Deodorizer":"Cleanup"}</td><td>${s.frequency||""}</td><td>${SUB_BADGE[s.status].label}</td><td style="font-weight:700">$${Number(s.amount||0).toFixed(2)}</td><td>${s.stripe_id||""}</td></tr>`).join("")
      : tab==="income"
      ? paidInvoices.filter(i=>Number(i.amount)>0).map(i=>`<tr><td>${i.date}</td><td>${i.client_name}</td><td>${i.invoice_number}</td><td style="color:green;font-weight:700">$${Number(i.amount).toFixed(2)}</td><td>Income</td></tr>`).join("")
        + creditInvoices.map(i=>`<tr><td>${i.date}</td><td>${i.client_name}</td><td>${i.invoice_number}</td><td style="color:#bf2600;font-weight:700">${Number(i.amount)>=0?"-":"+"}$${Math.abs(Number(i.amount)).toFixed(2)}</td><td>${Number(i.amount)>=0?"Credit":"Credit reversal"}</td></tr>`).join("")
        + rangeRefunds.map(r=>`<tr><td>${r.created_at?.split("T")[0]||""}</td><td>${r.client_name}</td><td>${r.invoice_number||""}</td><td style="color:#bf2600;font-weight:700">-$${Number(r.amount).toFixed(2)}</td><td>Refund</td></tr>`).join("")
      : tab==="payroll"
      ? (technicians||[]).filter(t=>t.active).map(t=>{const assigned=(clients||[]).filter(c=>c.technician_id===t.id&&c.active).length;return `<tr><td>${t.name}</td><td>${assigned}</td><td>${techWorkedHours(t)} hrs</td><td>$${techRate(t).toFixed(2)}/hr</td><td style="font-weight:700">$${techGross(t).toFixed(2)}</td></tr>`;}).join("")
      : tab==="credits"
      ? creditInvoices.map(i=>`<tr><td>${i.date}</td><td>${i.client_name}</td><td>${i.invoice_number}</td><td style="font-weight:700;color:${Number(i.amount)>=0?"#ea580c":"#36b37e"}">${Number(i.amount)>=0?"-":"+"}$${Math.abs(Number(i.amount)).toFixed(2)}</td><td>${i.notes||""}</td></tr>`).join("")
      : tab==="tax"
      ? [["Gross Revenue",totalIncome],["Credits Issued",-totalCredits],["Refunds Issued",-totalRefunds],["Net Revenue",netIncome],["Total Wages",-totalWages],["Net Profit",netProfit]].map(([k,v])=>`<tr><td>${k}</td><td style="font-weight:700">${v<0?"-":""}$${Math.abs(v).toFixed(2)}</td></tr>`).join("")
      : rangeRefunds.map(r=>`<tr><td>${r.created_at?.split("T")[0]||""}</td><td>${r.client_name}</td><td>${r.invoice_number||""}</td><td style="color:#bf2600;font-weight:700">-$${Number(r.amount).toFixed(2)}</td><td>${r.reason||""}</td></tr>`).join("");

    win.document.write(`<!DOCTYPE html><html><head><style>
      body{font-family:Inter,Arial,sans-serif;padding:32px;color:#1a1f36}
      h1{font-size:22px;color:#072C76;margin-bottom:4px}
      .sub{font-size:13px;color:#8993a4;margin-bottom:24px}
      table{width:100%;border-collapse:collapse;margin-bottom:20px}
      th{background:#072C76;color:#fff;padding:8px 12px;text-align:left;font-size:12px}
      td{padding:8px 12px;border-bottom:1px solid #f0f2f5;font-size:13px}
      .summary{display:flex;gap:20px;margin-bottom:24px}
      .sum-card{background:#f5f6f8;border-radius:8px;padding:14px 18px;flex:1}
      .sum-label{font-size:11px;color:#8993a4;text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px}
      .sum-value{font-size:20px;font-weight:700;color:#072C76}
    </style></head><body>
      <h1><svg width="22" height="22" viewBox="0 0 40 40" style="vertical-align:middle;margin-right:8px"><rect width="40" height="40" rx="11" fill="#0A84FF"/><circle cx="20" cy="11.8" r="2.6" fill="#fff"/><path d="M11 17.8a9 9 0 0 0 18 0" fill="none" stroke="#fff" stroke-width="3.3" stroke-linecap="round"/><path d="M20 26.8v4.4" stroke="#fff" stroke-width="3.3" stroke-linecap="round"/></svg>iScoopCRM — ${tab.charAt(0).toUpperCase()+tab.slice(1)} Report</h1>
      <div class="sub">Period: ${from} to ${to}</div>
      ${tab==="income"?`<div class="summary">
        <div class="sum-card"><div class="sum-label">Total Income</div><div class="sum-value" style="color:green">$${totalIncome.toFixed(2)}</div></div>
        <div class="sum-card"><div class="sum-label">Credits + Refunds</div><div class="sum-value" style="color:#bf2600">-$${(totalCredits+totalRefunds).toFixed(2)}</div></div>
        <div class="sum-card"><div class="sum-label">Net Income</div><div class="sum-value">$${netIncome.toFixed(2)}</div></div>
      </div>`:""}
      ${tab==="payroll"?`<div class="summary">
        <div class="sum-card"><div class="sum-label">Total Gross Payroll</div><div class="sum-value">$${totalWages.toFixed(2)}</div></div>
      </div>`:""}
      <table><thead><tr>
        ${tab==="subs"?"<th>Client</th><th>Type</th><th>Frequency</th><th>Status</th><th>Monthly</th><th>Stripe ID</th>":""}
        ${tab==="income"?"<th>Date</th><th>Client</th><th>Invoice #</th><th>Amount</th><th>Type</th>":""}
        ${tab==="payroll"?"<th>Technician</th><th>Clients</th><th>Hours</th><th>Rate</th><th>Gross Pay</th>":""}
        ${tab==="credits"?"<th>Date</th><th>Client</th><th>Invoice #</th><th>Credit Amount</th><th>Notes</th>":""}
        ${tab==="tax"?"<th>Metric</th><th>Amount</th>":""}
        ${tab==="refunds"?"<th>Date</th><th>Client</th><th>Invoice #</th><th>Amount</th><th>Reason</th>":""}
      </tr></thead><tbody>${rows}</tbody></table>
    </body></html>`);
    win.document.close();
    setTimeout(()=>win.print(),500);
  }

  return (
    <div style={ACTIVE_THEME === "apple-native" ? { maxWidth: 1200, margin: "0 auto" } : undefined}>
      {/* Summary KPIs — Net Income leads; each carries a period-over-period delta chip. */}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1.5fr 1fr 1fr 1fr",gap:14,marginBottom:18}}>
        {[
          {label:"Net income",     cur:netIncome,    prev:prevNet,     neg:false, invert:false, hero:true,  sub:"after credits & refunds"},
          {label:"Total income",   cur:totalIncome,  prev:prevIncome,  neg:false, invert:false, hero:false, sub:`${paidInvoices.filter(i=>Number(i.amount)>0).length} paid`},
          {label:"Refunds issued", cur:totalRefunds, prev:prevRefunds, neg:true,  invert:true,  hero:false, sub:`${rangeRefunds.length} refunds`},
          {label:"Credits issued", cur:totalCredits, prev:prevCredits, neg:true,  invert:true,  hero:false, sub:`${creditInvoices.length} credits`},
        ].map(c=>{
          const d = c.cur - c.prev;
          const pct = c.prev !== 0 ? Math.round((d / c.prev) * 100) : null;
          const good = d === 0 ? null : (c.invert ? d < 0 : d > 0); // refunds/credits rising = bad (red)
          const chip = good === null ? {bg:"rgba(0,0,0,.05)",fg:"var(--text2)"} : good ? {bg:"rgba(26,138,82,.12)",fg:"var(--ok)"} : {bg:"rgba(215,50,43,.12)",fg:"var(--danger)"};
          const money = (c.neg && c.cur !== 0 ? "−$" : "$") + Math.abs(c.cur).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
          const valColor = c.hero && netIncome < 0 ? "var(--danger)" : "var(--text)";
          return (
            <div key={c.label} style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:14,padding:c.hero?"16px 18px":"15px 16px",boxShadow:"var(--shadow-sm)"}}>
              <div style={{fontSize:11.5,fontWeight:500,letterSpacing:".04em",textTransform:"uppercase",color:"var(--text2)"}}>{c.label}</div>
              <div style={{fontSize:c.hero?42:27,fontWeight:600,letterSpacing:"-.02em",fontVariantNumeric:"tabular-nums",marginTop:6,lineHeight:1,color:valColor}}>{money}</div>
              <div style={{display:"flex",alignItems:"center",gap:7,marginTop:10,flexWrap:"wrap"}}>
                <span style={{display:"inline-flex",alignItems:"center",gap:4,background:chip.bg,color:chip.fg,fontSize:12,fontWeight:600,padding:"3px 8px",borderRadius:6,fontVariantNumeric:"tabular-nums"}}>
                  {d===0?"—":d>0?"▲":"▼"}{pct===null?(d===0?" 0%":" new"):` ${Math.abs(pct)}%`}
                </span>
                <span style={{fontSize:12,color:"var(--text2)"}}>{c.hero?`vs ${compareLabel}`:c.sub}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Controls — segmented period + compare-to, quiet export */}
      {/* Phone: the segmented groups are the controls that change every number on the page —
          stretch them to the full width with 36px+ buttons; exports become a clear action row. */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:16,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:isMobile?10:12,flexWrap:"wrap",width:isMobile?"100%":undefined}}>
          <div style={{display:"inline-flex",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:9,padding:2,gap:2,width:isMobile?"100%":undefined}}>
            {[["week","Week"],["month","Month"],["year","Year"],["custom","Custom"]].map(([val,label])=>(
              <button key={val} type="button" onClick={()=>setPeriod(val)}
                style={{border:"none",borderRadius:7,padding:isMobile?"9px 8px":"5px 12px",fontSize:isMobile?14:12.5,fontWeight:500,cursor:"pointer",minHeight:isMobile?36:undefined,flex:isMobile?1:undefined,
                  background:period===val?"var(--surface)":"transparent",color:period===val?"var(--text)":"var(--text2)",boxShadow:period===val?"var(--shadow-sm)":"none"}}>{label}</button>
            ))}
          </div>
          {period==="custom"&&(
            <div className="flex gap-2 items-center" style={isMobile?{width:"100%",flexWrap:"wrap"}:undefined}>
              <ClickableDate value={customFrom} onChange={setCustomFrom} width={isMobile?undefined:150} style={isMobile?{flex:"1 1 130px",minWidth:0,minHeight:44}:undefined}/>
              <span style={{color:"var(--text3)"}}>to</span>
              <ClickableDate value={customTo}   onChange={setCustomTo}   width={isMobile?undefined:150} style={isMobile?{flex:"1 1 130px",minWidth:0,minHeight:44}:undefined}/>
            </div>
          )}
          <span style={{fontSize:12,color:"var(--text2)"}}>Compare to</span>
          <div style={{display:"inline-flex",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:9,padding:2,gap:2,flex:isMobile?1:undefined}}>
            {[["period","Prev period"],["year","Last year"]].map(([val,label])=>(
              <button key={val} type="button" onClick={()=>setCompareMode(val)}
                style={{border:"none",borderRadius:7,padding:isMobile?"9px 8px":"5px 10px",fontSize:isMobile?14:12,fontWeight:500,cursor:"pointer",minHeight:isMobile?36:undefined,flex:isMobile?1:undefined,whiteSpace:"nowrap",
                  background:compareMode===val?"var(--surface)":"transparent",color:compareMode===val?"var(--text)":"var(--text2)",boxShadow:compareMode===val?"var(--shadow-sm)":"none"}}>{label}</button>
            ))}
          </div>
        </div>
        <div style={{display:"flex",gap:8,width:isMobile?"100%":undefined}}>
          <button className="btn btn-ghost btn-sm" style={isMobile?{flex:1,justifyContent:"center",minHeight:40}:undefined} onClick={downloadCSV}>{isMobile?"⬇️ Export CSV":"CSV"}</button>
          <button className="btn btn-ghost btn-sm" style={isMobile?{flex:1,justifyContent:"center",minHeight:40}:undefined} onClick={downloadPDF}>{isMobile?"📄 Export PDF":"PDF"}</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[["income","Income"],["subs","Subscriptions"],["payroll","Payroll"],["refunds","Refunds"],["credits","Credits"],["tax","Tax"]].map(([key,label])=>(
          <div key={key} className={`tab${tab===key?" active":""}`} onClick={()=>setTab(key)}>{label}</div>
        ))}
      </div>

      <div className="calm-pane">
      {/* ── INCOME TAB ── */}
      {tab==="income" && (
        <div className="card">
          <div className="card-title">Income — {from} to {to}</div>
          {incomeSeries.some(p => p.amount > 0) && (
            <TrendChart series={incomeSeries} prevSeries={incomePrevSeries} compareOn={true} compareLabel={compareLabel} fmt={fmtMoney} />
          )}
          {isMobile ? (
            <div>
              {paidInvoices.filter(i=>Number(i.amount)>0).sort((a,b)=>b.date.localeCompare(a.date)).map(i=>(
                <MobileRow key={i.id} title={i.client_name} amount={`$${Number(i.amount).toFixed(2)}`}
                  meta={[i.date, i.invoice_number, i.frequency && (i.frequency[0].toUpperCase()+i.frequency.slice(1))].filter(Boolean).join(" · ")}/>
              ))}
              {paidInvoices.filter(i=>Number(i.amount)>0).length===0&&<div className="empty"><p>No income in this period</p></div>}
            </div>
          ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Client</th><th>Invoice #</th><th>Service</th><th>Amount</th></tr></thead>
              <tbody>
                {paidInvoices.filter(i=>Number(i.amount)>0).sort((a,b)=>b.date.localeCompare(a.date)).map(i=>(
                  <tr key={i.id}>
                    <td style={{fontSize:12}}>{i.date}</td>
                    <td style={{fontWeight:600}}>{i.client_name}</td>
                    <td style={{fontSize:12}}>{i.invoice_number}</td>
                    <td style={{fontSize:12,textTransform:"capitalize"}}>{i.frequency}</td>
                    <td style={{fontWeight:500,color:"var(--text)",textAlign:"right",fontVariantNumeric:"tabular-nums"}}>${Number(i.amount).toFixed(2)}</td>
                  </tr>
                ))}
                {paidInvoices.filter(i=>Number(i.amount)>0).length===0&&<tr><td colSpan={5}><div className="empty"><p>No income in this period</p></div></td></tr>}
              </tbody>
            </table>
          </div>
          )}
          <div style={{display:"flex",justifyContent:"flex-end",borderTop:"1px solid var(--border)",paddingTop:12,marginTop:8}}>
            <span style={{fontWeight:600,fontSize:17,color:"var(--text)",fontVariantNumeric:"tabular-nums"}}>Total: ${totalIncome.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* ── SUBSCRIPTIONS TAB ── */}
      {tab==="subs" && (
        <div className="card">
          <div className="card-title">Subscriptions</div>
          <div style={{fontSize:12,color:"#8993a4",marginBottom:12}}>
            Each client's recurring cleanup plan plus any deodorizer add-on — one row per Stripe subscription (current state, not date-ranged).
          </div>
          {/* status filter */}
          <div style={{display:"flex",gap:isMobile?10:8,flexWrap:"wrap",marginBottom:10}}>
            {[["all","All"],["active","Active"],["paused","Paused"],["past_due","Past due"],["canceled","Canceled"]].map(([val,label])=>(
              <button key={val} onClick={()=>setSubStatus(val)} className={`btn btn-sm ${subStatus===val?"btn-primary":"btn-ghost"}`}>
                {label} <span style={{opacity:.65,marginLeft:4}}>{subStatusCounts[val]||0}</span>
              </button>
            ))}
          </div>
          {/* type + search */}
          <div className="flex gap-2" style={{marginBottom:12,flexWrap:"wrap",alignItems:"center",gap:isMobile?10:undefined}}>
            {[["all","All types"],["cleanup","🧹 Cleanup"],["deo","💨 Deodorizer"]].map(([val,label])=>(
              <button key={val} onClick={()=>setSubType(val)} className={`btn btn-sm ${subType===val?"btn-primary":"btn-ghost"}`}>{label}</button>
            ))}
            <input value={subSearch} onChange={e=>setSubSearch(e.target.value)} placeholder="Search client or subscription ID…" style={{flex:1,minWidth:isMobile?0:200,width:isMobile?"100%":undefined}}/>
          </div>
          {isMobile ? (
            <div>
              {filteredSubs.map(s=>(
                <MobileRow key={s.key} title={s.client_name} amount={`$${Number(s.amount||0).toFixed(2)}/mo`}
                  badge={<span className="badge" style={{background:SUB_BADGE[s.status].bg,color:SUB_BADGE[s.status].color,flexShrink:0}}>{SUB_BADGE[s.status].label}</span>}
                  meta={`${s.type==="deo"?"💨 Deodorizer":"🧹 Cleanup"} · ${s.frequency ? s.frequency[0].toUpperCase()+s.frequency.slice(1) : "—"}`}>
                  {s.stripe_id && <div style={mobileIdStyle}>{s.stripe_id}</div>}
                </MobileRow>
              ))}
              {filteredSubs.length===0&&<div className="empty"><p>No subscriptions match</p></div>}
            </div>
          ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Client</th><th>Type</th><th>Frequency</th><th>Status</th><th>Monthly</th><th>Stripe ID</th></tr></thead>
              <tbody>
                {filteredSubs.map(s=>(
                  <tr key={s.key}>
                    <td style={{fontWeight:600}}>{s.client_name}</td>
                    <td style={{fontSize:12}}>{s.type==="deo"?"💨 Deodorizer":"🧹 Cleanup"}</td>
                    <td style={{fontSize:12,textTransform:"capitalize"}}>{s.frequency||"—"}</td>
                    <td><span className="badge" style={{background:SUB_BADGE[s.status].bg,color:SUB_BADGE[s.status].color}}>{SUB_BADGE[s.status].label}</span></td>
                    <td style={{fontWeight:700,color:NAVY}}>${Number(s.amount||0).toFixed(2)}</td>
                    <td style={{fontSize:10,color:"#b3bac5"}}>{s.stripe_id||"—"}</td>
                  </tr>
                ))}
                {filteredSubs.length===0&&<tr><td colSpan={6}><div className="empty"><p>No subscriptions match</p></div></td></tr>}
              </tbody>
            </table>
          </div>
          )}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,borderTop:"2px solid #e3e8ef",paddingTop:12,marginTop:8}}>
            <span style={{fontSize:12,color:"#8993a4"}}>{filteredSubs.length} of {subs.length} subscriptions</span>
            <span style={{fontFamily:"Inter",fontWeight:800,fontSize:18,color:NAVY}}>Active MRR: ${activeMRR.toFixed(2)}/mo</span>
          </div>
        </div>
      )}

      {/* ── REFUNDS TAB ── */}
      {tab==="refunds" && (
        <div className="card">
          <div className="card-title">Refunds — {from} to {to}</div>
          {isMobile ? (
            <div>
              {rangeRefunds.sort((a,b)=>b.created_at?.localeCompare(a.created_at)).map(r=>(
                <MobileRow key={r.id} title={r.client_name} amount={`-$${Number(r.amount).toFixed(2)}`} amountColor={DANGER}
                  meta={[r.created_at?.split("T")[0], r.invoice_number].filter(Boolean).join(" · ")}>
                  {r.reason && <div style={{fontSize:12,color:"#42526e",marginTop:4,lineHeight:1.4}}>{r.reason}</div>}
                  {r.stripe_refund_id && <div style={mobileIdStyle}>{r.stripe_refund_id}</div>}
                </MobileRow>
              ))}
              {rangeRefunds.length===0&&<div className="empty"><p>No refunds in this period</p></div>}
            </div>
          ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Client</th><th>Invoice #</th><th>Amount</th><th>Reason</th><th>Stripe ID</th></tr></thead>
              <tbody>
                {rangeRefunds.sort((a,b)=>b.created_at?.localeCompare(a.created_at)).map(r=>(
                  <tr key={r.id}>
                    <td style={{fontSize:12}}>{r.created_at?.split("T")[0]}</td>
                    <td style={{fontWeight:600}}>{r.client_name}</td>
                    <td style={{fontSize:12}}>{r.invoice_number}</td>
                    <td style={{fontWeight:700,color:DANGER}}>-${Number(r.amount).toFixed(2)}</td>
                    <td style={{fontSize:12,color:"#42526e"}}>{r.reason||"—"}</td>
                    <td style={{fontSize:10,color:"#b3bac5"}}>{r.stripe_refund_id||"—"}</td>
                  </tr>
                ))}
                {rangeRefunds.length===0&&<tr><td colSpan={6}><div className="empty"><p>No refunds in this period</p></div></td></tr>}
              </tbody>
            </table>
          </div>
          )}
          {rangeRefunds.length>0&&<div style={{display:"flex",justifyContent:"flex-end",borderTop:"2px solid #e3e8ef",paddingTop:12,marginTop:8}}>
            <span style={{fontFamily:"Inter",fontWeight:800,fontSize:18,color:DANGER}}>Total: -${totalRefunds.toFixed(2)}</span>
          </div>}
        </div>
      )}

      {/* ── CREDITS TAB ── */}
      {tab==="credits" && (
        <div className="card">
          <div className="card-title">Credits Issued — {from} to {to}</div>
          <div style={{fontSize:12,color:"#8993a4",marginBottom:12}}>These are credits applied when a visit was skipped and the client was credited for that visit.</div>
          {isMobile ? (
            <div>
              {creditInvoices.sort((a,b)=>b.date.localeCompare(a.date)).map(i=>(
                <MobileRow key={i.id} title={i.client_name}
                  amount={`${Number(i.amount)>=0?"-":"+"}$${Math.abs(Number(i.amount)).toFixed(2)}`} amountColor={Number(i.amount)>=0?"#ea580c":"#36b37e"}
                  meta={`${i.date} · ${i.invoice_number}`}>
                  {i.notes && <div style={{fontSize:12,color:"#42526e",marginTop:4,lineHeight:1.4}}>{i.notes}</div>}
                </MobileRow>
              ))}
              {creditInvoices.length===0&&<div className="empty"><p>No credits issued in this period</p></div>}
            </div>
          ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Client</th><th>Invoice #</th><th>Credit Amount</th><th>Notes</th></tr></thead>
              <tbody>
                {creditInvoices.sort((a,b)=>b.date.localeCompare(a.date)).map(i=>(
                  <tr key={i.id}>
                    <td style={{fontSize:12}}>{i.date}</td>
                    <td style={{fontWeight:600}}>{i.client_name}</td>
                    <td style={{fontSize:12}}>{i.invoice_number}</td>
                    <td style={{fontWeight:700,color:Number(i.amount)>=0?"#ea580c":"#36b37e"}}>{Number(i.amount)>=0?"-":"+"}${Math.abs(Number(i.amount)).toFixed(2)}</td>
                    <td style={{fontSize:12,color:"#42526e"}}>{i.notes||"—"}</td>
                  </tr>
                ))}
                {creditInvoices.length===0&&<tr><td colSpan={5}><div className="empty"><p>No credits issued in this period</p></div></td></tr>}
              </tbody>
            </table>
          </div>
          )}
          {creditInvoices.length>0&&<div style={{display:"flex",justifyContent:"flex-end",borderTop:"2px solid #e3e8ef",paddingTop:12,marginTop:8}}>
            <span style={{fontFamily:"Inter",fontWeight:800,fontSize:18,color:"#ea580c"}}>Total Credits: -${totalCredits.toFixed(2)}</span>
          </div>}
        </div>
      )}

      {/* ── PAYROLL TAB ── */}
      {tab==="payroll" && (
        <div className="card">
          <div className="card-title">Payroll — {from} to {to}</div>
          <div style={{fontSize:12,color:"#8993a4",marginBottom:16}}>
            Hours are calculated automatically for the period above — each day counts from a tech's first job start to their last job end. Set each tech's hourly rate; pay = hours × rate. (Profit lives on the Tax tab.)
          </div>
          {isMobile ? (
            <div>
              {(technicians||[]).filter(t=>t.active).map(t=>{
                const assigned = (clients||[]).filter(c=>c.technician_id===t.id&&c.active).length;
                const h = techHours[t.id]||{rate:0};
                return (
                  <MobileRow key={t.id} title={t.name} amount={`$${techGross(t).toFixed(2)}`} amountColor={NAVY}
                    meta={<>
                      {assigned} client{assigned===1?"":"s"} · <span style={{fontWeight:600,color:"var(--text)"}}>{techWorkedHours(t)} hrs</span>
                      {incompleteTechs[t.id] && <button type="button" onClick={()=>showToast&&showToast(INCOMPLETE_HOURS_NOTE,"error")} style={{background:"none",border:"none",padding:"0 6px",marginLeft:2,color:"#B26B00",fontSize:14,cursor:"pointer",lineHeight:1}} aria-label="Some hours are missing">⚠</button>}
                    </>}>
                    <label style={{display:"flex",alignItems:"center",gap:6,marginTop:8,fontSize:12,color:"#8993a4"}}>
                      Rate $
                      <input type="number" value={h.rate||0} min="0" step="0.5" inputMode="decimal"
                        onChange={e=>setTechHours(prev=>({...prev,[t.id]:{...(prev[t.id]||{}),rate:e.target.value}}))}
                        onBlur={e=>saveTechField(t.id,"hourly_rate",e.target.value)}
                        style={{width:96,minHeight:40,border:"1px solid #dfe1e6",borderRadius:8,padding:"6px 10px",fontWeight:600}}/>
                      /hr
                    </label>
                  </MobileRow>
                );
              })}
              {(technicians||[]).filter(t=>t.active).length===0&&<div className="empty"><p>No active technicians</p></div>}
              {Object.values(incompleteTechs).some(Boolean) && (
                <div style={{fontSize:12,color:"#8A5A06",background:"#FEF6E7",border:"1px solid #F6D88A",borderRadius:10,padding:"9px 12px",marginTop:12,lineHeight:1.45}}>
                  ⚠ {INCOMPLETE_HOURS_NOTE}
                </div>
              )}
            </div>
          ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Technician</th><th>Clients</th><th>Hours</th><th>Rate ($/hr)</th><th>Gross Pay</th>
              </tr></thead>
              <tbody>
                {(technicians||[]).filter(t=>t.active).map(t=>{
                  const assigned = (clients||[]).filter(c=>c.technician_id===t.id&&c.active).length;
                  const h = techHours[t.id]||{rate:0};
                  return (
                    <tr key={t.id} style={{borderTop:"1px solid #f0f2f5"}}>
                      <td style={{padding:"12px",fontWeight:600,fontSize:13}}>{t.name}</td>
                      <td>{assigned}</td>
                      <td style={{fontWeight:600}} title="First job start → last job end, summed across the period">
                        {techWorkedHours(t)} <span style={{fontSize:11,color:"#8993a4"}}>hrs</span>
                        {incompleteTechs[t.id] && <span title={INCOMPLETE_HOURS_NOTE} onClick={()=>showToast&&showToast(INCOMPLETE_HOURS_NOTE,"error")} style={{marginLeft:5,color:"#B26B00",cursor:"help"}}>⚠</span>}
                      </td>
                      <td>
                        <span style={{color:"#8993a4"}}>$</span>
                        <input type="number" value={h.rate||0} min="0" step="0.5"
                          onChange={e=>setTechHours(prev=>({...prev,[t.id]:{...(prev[t.id]||{}),rate:e.target.value}}))}
                          onBlur={e=>saveTechField(t.id,"hourly_rate",e.target.value)}
                          style={{width:70,border:"1px solid #dfe1e6",borderRadius:6,padding:"6px 8px",fontSize:13,fontWeight:600}}/>
                        <span style={{fontSize:11,color:"#8993a4",marginLeft:4}}>/hr</span>
                      </td>
                      <td style={{fontWeight:700,color:NAVY}}>${techGross(t).toFixed(2)}</td>
                    </tr>
                  );
                })}
                {(technicians||[]).filter(t=>t.active).length===0&&<tr><td colSpan={5}><div className="empty"><p>No active technicians</p></div></td></tr>}
              </tbody>
            </table>
          </div>
          )}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,borderTop:"2px solid #e3e8ef",paddingTop:12,marginTop:8}}>
            <span style={{fontSize:12,color:"#8993a4"}}>Total gross payroll for {from} to {to}</span>
            <span style={{fontFamily:"Inter",fontWeight:800,fontSize:18,color:NAVY}}>${totalWages.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* ── TAX TAB ── */}
      {tab==="tax" && (
        <div>
          <div className="stats-row" style={{marginBottom:16}}>
            {[
              {label:"Gross Revenue",  value:`$${totalIncome.toFixed(2)}`,   sub:"Paid invoices in period"},
              {label:"Credits Issued", value:`-$${totalCredits.toFixed(2)}`, sub:"Deducted from revenue"},
              {label:"Refunds Issued", value:`-$${totalRefunds.toFixed(2)}`, sub:"Deducted from revenue"},
              {label:"Net Revenue",    value:`$${netIncome.toFixed(2)}`,     sub:"After credits & refunds"},
              {label:"Total Wages",    value:`-$${totalWages.toFixed(2)}`,   sub:"Business expense"},
              {label:"Net Profit",     value:`$${netProfit.toFixed(2)}`,     sub:"Before other expenses"},
            ].map(s=>(
              <div key={s.label} className="stat-card">
                <div className="stat-accent"/>
                <div className="stat-label">{s.label}</div>
                <div className="stat-value" style={{fontSize:16}}>{s.value}</div>
                <div className="stat-sub">{s.sub}</div>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="card-title">Quarterly Breakdown — {now.getFullYear()}</div>
            <div className={isMobile?undefined:"table-wrap"}>
              {isMobile ? (
                <div>
                  {[
                    {q:"Q1",label:"Jan – Mar",qFrom:`${now.getFullYear()}-01-01`,qTo:`${now.getFullYear()}-03-31`},
                    {q:"Q2",label:"Apr – Jun",qFrom:`${now.getFullYear()}-04-01`,qTo:`${now.getFullYear()}-06-30`},
                    {q:"Q3",label:"Jul – Sep",qFrom:`${now.getFullYear()}-07-01`,qTo:`${now.getFullYear()}-09-30`},
                    {q:"Q4",label:"Oct – Dec",qFrom:`${now.getFullYear()}-10-01`,qTo:`${now.getFullYear()}-12-31`},
                  ].map(({q,label,qFrom,qTo})=>{
                    const qInv  = invoices.filter(i=>i.status==="paid"&&i.date>=qFrom&&i.date<=qTo&&Number(i.amount)>0);
                    const qRef  = refunds.filter(r=>(r.created_at?.split("T")[0]||"")>=qFrom&&(r.created_at?.split("T")[0]||"")<=qTo);
                    const gross = qInv.reduce((s,i)=>s+Number(i.amount),0);
                    const refs  = qRef.reduce((s,r)=>s+Number(r.amount),0);
                    return (
                      <MobileRow key={q} title={`${q} · ${label}`} amount={`$${(gross-refs).toFixed(2)}`} amountColor={NAVY}
                        meta={<>Gross <span style={{color:"#006644",fontWeight:600}}>${gross.toFixed(2)}</span> · Refunds <span style={{color:"#bf2600"}}>-${refs.toFixed(2)}</span></>}/>
                    );
                  })}
                  <div style={{fontSize:11,color:"var(--text3)",marginTop:8}}>Net revenue per quarter (gross − refunds).</div>
                </div>
              ) : (
              <table>
                <thead><tr><th>Quarter</th><th>Period</th><th>Gross Revenue</th><th>Refunds</th><th>Net Revenue</th></tr></thead>
                <tbody>
                  {[
                    {q:"Q1",qFrom:`${now.getFullYear()}-01-01`,qTo:`${now.getFullYear()}-03-31`},
                    {q:"Q2",qFrom:`${now.getFullYear()}-04-01`,qTo:`${now.getFullYear()}-06-30`},
                    {q:"Q3",qFrom:`${now.getFullYear()}-07-01`,qTo:`${now.getFullYear()}-09-30`},
                    {q:"Q4",qFrom:`${now.getFullYear()}-10-01`,qTo:`${now.getFullYear()}-12-31`},
                  ].map(({q,qFrom,qTo})=>{
                    const qInv  = invoices.filter(i=>i.status==="paid"&&i.date>=qFrom&&i.date<=qTo&&Number(i.amount)>0);
                    const qRef  = refunds.filter(r=>(r.created_at?.split("T")[0]||"")>=qFrom&&(r.created_at?.split("T")[0]||"")<=qTo);
                    const gross = qInv.reduce((s,i)=>s+Number(i.amount),0);
                    const refs  = qRef.reduce((s,r)=>s+Number(r.amount),0);
                    return (
                      <tr key={q}>
                        <td style={{fontWeight:700,color:NAVY}}>{q}</td>
                        <td style={{fontSize:12,color:"#42526e"}}>{qFrom} to {qTo}</td>
                        <td style={{fontWeight:600,color:"#006644"}}>${gross.toFixed(2)}</td>
                        <td style={{color:"#bf2600"}}>-${refs.toFixed(2)}</td>
                        <td style={{fontWeight:700,color:NAVY}}>${(gross-refs).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:12}}>
            <button className="btn btn-ghost btn-sm" style={isMobile?{flex:1,justifyContent:"center",minHeight:40}:undefined} onClick={downloadCSV}>⬇️ Export CSV</button>
            <button className="btn btn-gold btn-sm" style={isMobile?{flex:1,justifyContent:"center",minHeight:40}:undefined} onClick={downloadPDF}>📄 Export PDF</button>
          </div>
        </div>
      )}
      </div>

    </div>
  );
}
